import React, { useEffect, useState, useRef } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import {
  selectedProvincesState,
  selectedPemdaState,
  sidebarisexpandAtom,
  selectedJenisTkdState,
  months,
  startMonthAtom,
  endMonthAtom,
} from "states/recoil";
import {
  Center,
  Spinner,
  Text,
  CardBody,
  CardHeader,
  Heading,
  IconButton,
  Flex,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Button,
} from "@chakra-ui/react";
import { HiChartBar, HiOutlineDownload, HiArrowsExpand } from "react-icons/hi";
import Chart from "react-apexcharts";
import { DownloadImage } from "components/functions/DownloadImage";
import { ExportToExcel } from "components/functions/ExportToExcel";
import { ToggleFullScreen } from "components/functions/ToggleFullscreen";
import { UseTogglePopUp } from "components/functions/UseTogglePopUp";
import { tahunAnggaranAtom } from "states/recoil";

function VerticalBarChart({ data, xaxisData, titleChart }) {
  //ref to downloadImage
  const chartContainerRef = useRef(null);

  //recoil-state
  const sidebarisexpandGet = useRecoilValue(sidebarisexpandAtom);
  const [selectedProvinces, setSelectedProvinces] = useRecoilState(
    selectedProvincesState
  );
  const [selectedPemda, setSelectedPemda] = useRecoilState(selectedPemdaState);
  const [selectedJenisTKD, setSelectedJenisTKD] = useRecoilState(
    selectedJenisTkdState
  );
  const monthArray = useRecoilValue(months);
  const [selectedStartMonth] = useRecoilState(startMonthAtom);
  const [selectedEndMonth] = useRecoilState(endMonthAtom);

  //local state
  const [isLoading, setIsLoading] = useState(true);
  const [rawData, setRawData] = useState([]);
  const { isOpen, openPopup, closePopup } = UseTogglePopUp();

  //chart state
  const [chartDataVerticalBar, setChartDataVerticalBar] = useState({
    categories: [],
    series: [],
    raw: { pagu: [], realisasi: [] },
    annotations: [],
  });

  //get the data
  useEffect(() => {
    setRawData(data);
  }, [data]);

  //Set Chart Data
  useEffect(() => {
    setIsLoading(true);

    if (
      !rawData.length ||
      !selectedProvinces.length ||
      !selectedPemda.length ||
      !selectedJenisTKD.length
    ) {
      setChartDataVerticalBar({
        categories: [],
        series: [],
        raw: { pagu: [], realisasi: [] },
        annotations: [],
      });
      return;
    }

    const filteredData = rawData.filter(
      (row) =>
        selectedProvinces.includes(row["NM PROV"]) &&
        selectedPemda.includes(row["NM PEMDA"]) &&
        selectedJenisTKD.includes(row["JENIS TKD"])
    );

    function calculateFilteredRealisasi(row, startMonth, endMonth) {
      let total = 0;
      const startIndex = monthArray.indexOf(startMonth);
      const endIndex = monthArray.indexOf(endMonth);

      for (let i = startIndex; i <= endIndex; i++) {
        const monthNumber = (i + 1).toString().padStart(2, "0");
        const key = `REALISASI ${monthNumber}`;
        total += parseFloat(row[key] || 0);
      }
      return total;
    }
    const grouped = {};
    filteredData.forEach((row) => {
      const jenis = row[xaxisData];
      if (!jenis) return;

      if (!grouped[jenis]) {
        grouped[jenis] = {
          "PAGU ALOKASI": 0,
          REALISASI: 0,
        };
      }

      grouped[jenis]["PAGU ALOKASI"] += parseFloat(row["PAGU ALOKASI"] || 0);
      grouped[jenis]["REALISASI"] += calculateFilteredRealisasi(
        row,
        selectedStartMonth,
        selectedEndMonth
      );
    });

    const categories = Object.keys(grouped);
    const fullPagu = [];
    const fullRealisasi = [];
    const paguData = [];
    const realisasiData = [];

    categories.forEach((key) => {
      if (!grouped[key]) return;
      const pagu = grouped[key]["PAGU ALOKASI"];
      const real = grouped[key]["REALISASI"];
      fullPagu.push(pagu);
      fullRealisasi.push(real);
      paguData.push(pagu / 1_000_000_000);
      realisasiData.push(real / 1_000_000_000);
    });

    const formatter = new Intl.NumberFormat("id-ID", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    const annotations = categories
      .map((cat, i) => [
        {
          x: cat,
          y: paguData[i],
          label: {
            text: `${formatter.format(paguData[i])} M`,
            style: {
              fontSize: "12px",
              fontWeight: "bold",
              color: "#000",
              background: "#fff",
            },
            offsetY: -10,
          },
          seriesIndex: 0,
        },
        {
          x: cat,
          y: realisasiData[i],
          label: {
            text: `${formatter.format(realisasiData[i])} M`,
            style: {
              fontSize: "12px",
              fontWeight: "bold",
              color: "#000",
              background: "#fff",
            },
            offsetY: -10,
          },
          seriesIndex: 1,
        },
      ])
      .flat();

    setChartDataVerticalBar({
      categories,
      series: [
        { name: "PAGU ALOKASI", data: paguData },
        { name: "REALISASI", data: realisasiData },
      ],
      raw: { pagu: fullPagu, realisasi: fullRealisasi },
      annotations,
    });
    setIsLoading(false);
  }, [
    rawData,
    selectedProvinces,
    selectedPemda,
    selectedJenisTKD,
    selectedEndMonth,
    selectedStartMonth,
    tahunAnggaranAtom,
  ]);

  //set chart options
  const chartOptions = {
    chart: {
      type: "bar",
      stacked: false,
      toolbar: { show: false },
      width: "100%",
    },
    plotOptions: { bar: { horizontal: false, columnWidth: "45%" } },
    dataLabels: { enabled: false },
    annotations: { points: chartDataVerticalBar.annotations },
    tooltip: {
      y: {
        formatter: (val, { seriesIndex, dataPointIndex }) => {
          const fullVal =
            seriesIndex === 0
              ? chartDataVerticalBar.raw.pagu[dataPointIndex]
              : chartDataVerticalBar.raw.realisasi[dataPointIndex];
          return (
            fullVal?.toLocaleString("id-ID", {
              style: "currency",
              currency: "IDR",
              maximumFractionDigits: 0,
            }) ?? "-"
          );
        },
      },
    },
    xaxis: {
      categories: chartDataVerticalBar.categories,
      labels: { rotate: -45 },
      width: "100%",
    },
    yaxis: { show: false },
    legend: { position: "bottom" },
    colors: ["#005599", "#00E396"],
  };

  return (
    <>
      <CardHeader
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Flex align="center" gap={4}>
          <IconButton icon={<HiChartBar />} aria-label="chart" size="sm" />
          <Heading size="sm">{titleChart}</Heading>
        </Flex>
        <Flex gap={4}>
          <IconButton
            icon={<HiArrowsExpand />}
            aria-label="export"
            size="sm"
            onClick={openPopup}
          />
          <Menu>
            <MenuButton as={Button} size="sm">
              <HiOutlineDownload />
            </MenuButton>
            <MenuList
              maxHeight="300px"
              overflowY="auto"
              minWidth="250px"
              zIndex={3}
            >
              <MenuItem
                onClick={() =>
                  DownloadImage(chartContainerRef.current, titleChart)
                }
                title={titleChart}
              >
                Unduh sebagai gambar (.PNG)
              </MenuItem>
              <MenuItem
                onClick={() =>
                  ExportToExcel(chartDataVerticalBar, `${titleChart}.xlsx`)
                }
              >
                Unduh sebagai spreadsheet (.xlsx)
              </MenuItem>
            </MenuList>
          </Menu>
        </Flex>
      </CardHeader>
      <CardBody ref={chartContainerRef}>
        {isLoading ? (
          <Center w="100%" h="500px">
            <Spinner size="xl" color="blue.500" />
          </Center>
        ) : chartDataVerticalBar.categories.length === 0 ? (
          <Center w="100%" h="500px">
            <Text>Tidak ada data yang ditampilkan.</Text>
          </Center>
        ) : (
          <Chart
            options={chartOptions}
            series={chartDataVerticalBar.series}
            type="bar"
            height={400}
            width="100%"
          />
        )}
      </CardBody>

      {isOpen && (
        <ToggleFullScreen onClose={closePopup} rawData={rawData}>
          {/* Render the expanded chart here */}
          <div
            style={{
              height: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
            }}
          >
            {isLoading ? (
              <Center w="100%" h="500px">
                <Spinner size="xl" color="blue.500" />
              </Center>
            ) : chartDataVerticalBar.categories.length === 0 ? (
              <Center w="100%" h="500px">
                <Text>Tidak ada data yang ditampilkan.</Text>
              </Center>
            ) : (
              <Center w="100%" h="100%">
                <Chart
                  options={chartOptions}
                  series={chartDataVerticalBar.series}
                  type="bar"
                  height="100%"
                  width="400%"
                />
              </Center>
            )}
          </div>
        </ToggleFullScreen>
      )}
    </>
  );
}

export default VerticalBarChart;
